import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { InfoHeader, PrimaryButton } from '../components';
import { Perfil } from '../types/perfil/Perfil';
import { perfilService } from '../services/PerfilService';
import toastService from '../utils/toastService';
import ProtecaoAcesso from '../components/ProtecaoAcesso';
import PerfilModal from '../components/modais/PerfilModal';
import ProtectedBasicMenu from '../components/ProtectedBasicMenu';
import ProtecaoBotao from '../components/ProtecaoBotao';

const CadastroPeris: React.FC = () => {
  
  // Estados principais
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [perfilSelecionado, setPerfilSelecionado] = useState<Perfil | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Estados de controle de UI
  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);

  useEffect(() => {
    carregarPerfis();
  }, []);

  const carregarPerfis = async () => {
    setLoading(true);
    try {
      const perfisCarregados = await perfilService.buscarTodos();
      setPerfis(perfisCarregados);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      toastService.error('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoPerfil = () => {
    setPerfilSelecionado(null);
    setModoEdicao(false);
    setModalAberto(true);
  };

  const handleEditarPerfil = (perfil: Perfil) => {
    setPerfilSelecionado(perfil);
    setModoEdicao(true);
    setModalAberto(true);
  };

  const handleVisualizarPerfil = (perfil: Perfil) => {
    setPerfilSelecionado(perfil);
    setModoEdicao(false);
    setModalAberto(true);
  };

  const handleDuplicarPerfil = async (perfil: Perfil) => {
    try {
      const novoNome = `${perfil.nome} - Cópia`;
      await perfilService.duplicarPerfil(perfil.guid!, novoNome);
      toastService.success('Perfil duplicado com sucesso!');
      await carregarPerfis();
    } catch (error) {
      console.error('Erro ao duplicar perfil:', error);
      toastService.error('Erro ao duplicar perfil');
    }
  };

  const handleSalvarPerfil = async (perfilData: Perfil) => {
    try {
      if (modoEdicao && perfilData.guid) {
        // Atualizar
        await perfilService.atualizar(perfilData.guid, perfilData);
        toastService.success('Perfil atualizado com sucesso!');
      } else {
        // Criar novo
        await perfilService.criar(perfilData);
        toastService.success('Perfil criado com sucesso!');
      }

      await carregarPerfis();
      setModalAberto(false);
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      toastService.error('Erro ao salvar perfil');
      throw error; // Para que o modal trate o erro
    }
  };

  const handleExcluirPerfil = async (perfil: Perfil) => {
    if (!perfil.guid) return;

    try {
      await perfilService.excluir(perfil.guid);
      toastService.success('Perfil excluído com sucesso!');
      await carregarPerfis();
      
      // Se estava editando o perfil excluído, fechar modal
      if (perfilSelecionado?.guid === perfil.guid) {
        setModalAberto(false);
        setPerfilSelecionado(null);
      }
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      toastService.error('Erro ao excluir perfil');
    }
  };

  return (
    <ProtecaoAcesso 
      tela="perfis"
      tipo="visualizacao"
    >
      <Box p={3}>
        <InfoHeader
            title="Gestão de Perfis de Acesso"
            message="Gerencie os perfis e suas permissões de acesso ao sistema."
            isImportant
        />

      <Grid container spacing={3}>
        {/* Lista de Perfis */}
        <Grid size={{ xs: 12 }}>
          <Box mb={2}>
            <PrimaryButton 
              onClick={handleNovoPerfil}
            >
              + Adicionar
            </PrimaryButton>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {perfis.map((perfil) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={perfil.guid}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box 
                          flex={1}
                          onClick={() => handleVisualizarPerfil(perfil)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography 
                            variant="h6"
                            sx={{
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.main'
                              }
                            }}
                          >
                            {perfil.nome}
                            {!perfil.ativo && (
                              <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                                (Inativo)
                              </Typography>
                            )}
                          </Typography>
                          {perfil.descricao && (
                            <Typography variant="body2" color="text.secondary">
                              {perfil.descricao}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {perfil.acessos?.length || 0} acessos configurados
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ProtecaoBotao tela="perfis" tipo="alteracao">
                            <Tooltip title="Duplicar perfil">
                              <IconButton 
                                onClick={() => handleDuplicarPerfil(perfil)} 
                                size="small"
                                color="primary"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ProtecaoBotao>
                          
                          <ProtectedBasicMenu
                            tela="perfis"
                            onEdit={() => handleEditarPerfil(perfil)}
                            onDelete={() => handleExcluirPerfil(perfil)}
                            deleteItemName={perfil.nome}
                            deleteConfirmTitle="Confirmar Exclusão de Perfil"
                            deleteConfirmMessage="Tem certeza que deseja excluir este perfil? Esta ação não pode ser desfeita e todos os usuários associados a este perfil perderão suas permissões específicas."
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Modal de Edição/Criação */}
      <PerfilModal
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        onSave={handleSalvarPerfil}
        perfil={perfilSelecionado}
        modoEdicao={modoEdicao}
      />
      </Box>
    </ProtecaoAcesso>
  );
};

export default CadastroPeris;